import React, { Fragment, useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useNavigate, Link } from 'react-router-dom';
import { ipaddress, port, API_URL, BASE_URL } from './Parametrizaciones.js';

import {
    Building2,
    Home,
    Key,
    FileText,
    Coins,
    CheckCircle2,
    User,
    MessageSquare,
    Calendar,
    ArrowRight,
    Search,
    MapPin,
    Download
} from 'lucide-react';

// ==========================================
// CONFIGURACIÓN DE DATOS
// ==========================================

const SERVICIOS = [
    {
        id: 1,
        icon: <Home className="text-primary" size={28} />,
        title: "Venta de Propiedades",
        desc: "Te acompañamos en todo el proceso de compra y venta de tu inmueble con una asesoría integral, personalizada y transparente. Nuestro equipo te guía en cada etapa para que vendas de forma rápida, segura y al mejor precio del mercado, o encuentres la propiedad ideal con el respaldo y la confianza que mereces."
    },
    {
        id: 2,
        icon: <Key className="text-primary" size={28} />,
        title: "Arrendamientos",
        desc: "Administramos tu inmueble con experiencia, transparencia y compromiso, para que disfrutes de una gestión segura y rentable. Si buscas una vivienda en arriendo, te asesoramos para encontrar la opción ideal para ti."
    },
    {
        id: 3,
        icon: <Building2 className="text-primary" size={28} />,
        title: "Gestión Comercial",
        desc: "Promocionamos tu inmueble y gestionamos la venta y el arrendamiento de viviendas, locales, oficinas, bodegas y todo tipo de propiedades en el Valle de Aburrá y sus alrededores, garantizando un servicio profesional, confiable y con total discreción."
    }
];

const ASESORES = [
    {
        id: 1,
        name: "Solicitud Comercial",
        role: "Director Comercial",
        img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400&h=500",
        social: { facebook: "#", instagram: "#", linkedin: "#" }
    },
    {
        id: 2,
        name: "Solicitud Personal",
        role: "Asesora de Comercial",
        img: "https://images.unsplash.com/photo-1545947313-93c756069e69?auto=format&fit=crop&q=80&w=400&h=500",
        social: { facebook: "#", instagram: "#", linkedin: "#" }
    },
];

const NOTICIAS = [
    {
        id: 1,
        title: "Aumento del canon de arrendamiento en Colombia",
        desc: "Conoce cómo se calcula el incremento del valor de los arriendos en el país, basado en la legislación vigente y el comportamiento del IPC.",
        date: "23 May, 2026",
        author: "Lonja de Propiedad Raíz",
        img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=600&h=400",
        url: "https://lonja.org.co/aumento-canon-arrendamiento-colombia/"
    },
    {
        id: 2,
        title: "Canon de arrendamiento 2026: ¿Cuánto puede subir y qué dice la ley?",
        desc: "Toda la información legal y los topes permitidos para el incremento del canon de arrendamiento durante este año en el territorio nacional.",
        date: "14 Ene, 2026",
        author: "Lonja de Propiedad Raíz",
        img: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&q=80&w=600&h=400",
        url: "https://lonja.org.co/canon-de-arrendamiento-2026-cuanto-puede-subir-y-que-dice-la-ley/"
    },
    {
        id: 3,
        title: "Ventajas de arrendar con una inmobiliaria en Colombia",
        desc: "Descubre por qué contar con el respaldo de profesionales asegura un proceso de arrendamiento tranquilo, legal y sin complicaciones para propietarios e inquilinos.",
        date: "02 Jun, 2026",
        author: "Ciencuadras Blog",
        img: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&q=80&w=600&h=400",
        url: "https://www.ciencuadras.com/blog/guia-para-arrendar-inmuebles/ventajas-de-arrendar-con-inmobiliaria-en-colombia"
    }
];

const FORMATOS_ARRENDAMIENTO = [
    {
        id: 1,
        title: "Solicitud Comercial",
        description: "Formulario para empresas, sociedades o establecimientos comerciales.",
        fileSize: "1.2 MB",
        fileUrl: "/documents/Solicitud-Comercial.pdf",
        type: "PDF"
    },
    {
        id: 2,
        title: "Solicitud Personal",
        description: "Formulario de solicitud para personas naturales e individuales.",
        fileSize: "1.5 MB",
        fileUrl: "/documents/Solicitud-Persona-Natural.pdf",
        type: "PDF"
    },
];

const MARCAS = [
    { id: 1, name: "FENALCO Antioquia" }
];

const Index = () => {
    const [tipoGestion, setTipoGestion] = useState('arriendo');
    const navigate = useNavigate();

    const [tipoInmueble, setTipoInmueble] = useState('');
    const [municipio, setMunicipio] = useState('');
    const [barrio, setBarrio] = useState('');
    const [rangoPrecio, setRangoPrecio] = useState('');
    const [codigoInmueble, setCodigoInmueble] = useState('');

    const clickToPropiedad = (i) => {
        navigate('/propiedades/' + i);
    };

    // Función principal para procesar y ejecutar la búsqueda
    const clickToSearch = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        const codigoLimpio = codigoInmueble.trim();

        // 1. Prioridad: Búsqueda exacta por código
        if (codigoLimpio !== '') {
            navigate(`/buscar?codigo=${encodeURIComponent(codigoLimpio)}`);
            return;
        }

        // 2. Búsqueda por Filtros Dinámicos
        const params = new URLSearchParams();

        if (tipoGestion) params.append('opcion_inmueble', tipoGestion);
        if (tipoInmueble) params.append('tipo_inmueble', tipoInmueble);
        if (municipio) params.append('municipio', municipio);
        if (barrio) params.append('barrio', barrio);
        if (rangoPrecio) params.append('rango_precio', rangoPrecio);

        const queryString = params.toString();

        if (queryString) {
            navigate(`/buscar?${queryString}`);
        } else {
            navigate('/buscar');
        }
    };

    const [listInmueble, setInmueble] = useState([]);

    const handleDownload = async (e, fileUrl, fileName) => {
        e.preventDefault();

        try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Error al acceder al archivo');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);

            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("No se pudo descargar automáticamente, abriendo en pestaña nueva:", error);
            window.open(fileUrl, '_blank');
        }
    };

    useEffect(() => {
        Modal.setAppElement('body');

        const cargarPropiedadesTop = async () => {
            try {
                const resCodigos = await fetch(`${API_URL}?action=obtenerCodigosTop`);
                if (!resCodigos.ok) throw new Error("Error obteniendo los códigos top");
                const dataCodigos = await resCodigos.json();

                if (!dataCodigos || (Array.isArray(dataCodigos) && dataCodigos.length === 0)) {
                    setInmueble([]);
                    return;
                }

                const objetoCodigos = Array.isArray(dataCodigos) ? dataCodigos[0] : dataCodigos;

                const resInmuebles = await fetch(`${API_URL}?action=topInmuebles`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(objetoCodigos)
                });

                if (!resInmuebles.ok) throw new Error("Error obteniendo el detalle de inmuebles");
                const dataInmuebles = await resInmuebles.json();

                setInmueble(Array.isArray(dataInmuebles) ? dataInmuebles : []);

            } catch (err) {
                console.error("Error en la carga de propiedades top:", err);
                setInmueble([]);
            }
        };

        cargarPropiedadesTop();
    }, []);

    return (
        <Fragment>
            <section id="aa-slider" className="position-relative w-100 overflow-hidden d-flex align-items-center" style={{ minHeight: '680px' }}>

                {/* Imagen de fondo */}
                <div className="position-absolute top-0 start-0 w-100 h-100 z-1">
                    <img
                        src="imagesmain/FondoPrincipal.jpg"
                        alt="Arrendamientos Superbienes Home"
                        className="w-100 h-100 object-fit-cover"
                    />
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{ backgroundColor: 'rgba(15, 23, 42, 0.4)' }}></div>
                </div>

                {/* Buscador Principal */}
                <div className="position-relative container h-100 d-flex align-items-center justify-content-center z-2 py-5">
                    <div className="w-100 mx-auto px-2" style={{ maxWidth: '1100px' }}>

                        <div className="text-center text-white mb-4 drop-shadow">
                            <h1 className="fw-bold display-5 mb-2">Encuentra tu próximo inmueble</h1>
                            <p className="lead opacity-90 d-none d-sm-block">Tu aliado inmobiliario en Medellín y todo el Valle de Aburrá</p>
                        </div>

                        <div
                            className="bg-white p-4 p-md-5 shadow-lg border-0 search-card-hero"
                            style={{
                                borderRadius: '32px',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)'
                            }}
                        >

                            {/* Pestañas Arriendo / Venta */}
                            <div className="d-flex justify-content-center mb-4">
                                <div
                                    className="p-1.5 d-inline-flex gap-1 rounded-pill shadow-inner"
                                    style={{ backgroundColor: '#2b2b2b', padding: '6px' }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setTipoGestion('arriendo')}
                                        className="border-0 px-4 px-md-5 py-2 fw-bold rounded-pill custom-tab-btn"
                                        style={{
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            backgroundColor: tipoGestion === 'arriendo' ? '#ffffff' : 'transparent',
                                            color: tipoGestion === 'arriendo' ? '#000000' : 'rgba(255,255,255,0.85)',
                                            boxShadow: tipoGestion === 'arriendo' ? '0px 4px 12px rgba(0, 0, 0, 0.25)' : 'none'
                                        }}
                                    >
                                        Arriendo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTipoGestion('venta')}
                                        className="border-0 px-4 px-md-5 py-2 fw-bold rounded-pill custom-tab-btn"
                                        style={{
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            backgroundColor: tipoGestion === 'venta' ? '#ffffff' : 'transparent',
                                            color: tipoGestion === 'venta' ? '#000000' : 'rgba(255,255,255,0.85)',
                                            boxShadow: tipoGestion === 'venta' ? '0px 4px 12px rgba(0, 0, 0, 0.25)' : 'none'
                                        }}
                                    >
                                        Venta
                                    </button>
                                </div>
                            </div>

                            {/* Formulario de Búsqueda */}
                            <form
                                onSubmit={clickToSearch}
                                className="p-3 border-0 shadow-sm"
                                style={{ backgroundColor: '#f4f5f7', borderRadius: '24px' }}
                            >
                                <div className="row g-2 align-items-end row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6">

                                    {/* 1. Tipo de inmueble */}
                                    <div className="col flex-column d-flex gap-1">
                                        <span className="px-2 text-muted fw-bold" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            TIPO DE INMUEBLE
                                        </span>
                                        <select
                                            value={tipoInmueble}
                                            onChange={(e) => {
                                                setTipoInmueble(e.target.value);
                                                if (e.target.value) setCodigoInmueble('');
                                            }}
                                            className="form-select bg-white border-0 text-dark rounded-pill px-3 py-2 shadow-sm style-select-custom"
                                            style={{ fontSize: '13.5px', height: '44px' }}
                                        >
                                            <option value="">Todos</option>
                                            <option value="Apartamento">Apartamento</option>
                                            <option value="Bodegas">Bodegas</option>
                                            <option value="Casas">Casas</option>
                                            <option value="Locales">Locales</option>
                                            <option value="Lotes">Lotes</option>
                                            <option value="Fincas">Fincas</option>
                                            <option value="Oficinas">Oficinas</option>
                                        </select>
                                    </div>

                                    {/* 2. Municipio */}
                                    <div className="col flex-column d-flex gap-1">
                                        <span className="px-2 text-muted fw-bold" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            MUNICIPIO
                                        </span>
                                        <select
                                            value={municipio}
                                            onChange={(e) => {
                                                setMunicipio(e.target.value);
                                                if (e.target.value) setCodigoInmueble('');
                                            }}
                                            className="form-select bg-white border-0 text-dark rounded-pill px-3 py-2 shadow-sm style-select-custom"
                                            style={{ fontSize: '13.5px', height: '44px' }}
                                        >
                                            <option value="">Todos</option>
                                            <option value="Medellin">Medellín</option>
                                            <option value="Envigado">Envigado</option>
                                            <option value="Sabaneta">Sabaneta</option>
                                            <option value="Rionegro">Rionegro</option>
                                            <option value="Bello">Bello</option>
                                            <option value="Itagüi">Itagüi</option>
                                            <option value="Guarne">Guarne</option>
                                            <option value="El Retiro">El Retiro</option>
                                            <option value="Marinilla">Marinilla</option>
                                            <option value="Llano Grande">Llano Grande</option>
                                        </select>
                                    </div>

                                    {/* 3. Barrio */}
                                    <div className="col flex-column d-flex gap-1">
                                        <span className="px-2 text-muted fw-bold" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            BARRIO
                                        </span>
                                        <select
                                            value={barrio}
                                            onChange={(e) => {
                                                setBarrio(e.target.value);
                                                if (e.target.value) setCodigoInmueble('');
                                            }}
                                            className="form-select bg-white border-0 text-dark rounded-pill px-3 py-2 shadow-sm style-select-custom"
                                            style={{ fontSize: '13.5px', height: '44px' }}
                                        >
                                            <option value="">Todos</option>
                                            <option value="El Poblado">El Poblado</option>
                                            <option value="Robledo">Robledo</option>
                                            <option value="Belen">Belen</option>
                                            <option value="Prado">Prado</option>
                                            <option value="Centro">Centro</option>
                                            <option value="Manrique">Manrique</option>
                                            <option value="Buenos Aires">Buenos Aires</option>
                                            <option value="Villa Hermosa">Villa Hermosa</option>
                                            <option value="La America">La America</option>
                                            <option value="Cabañitas">Cabañitas</option>
                                            <option value="Pedregal">Pedregal</option>
                                            <option value="San Javier">San Javier</option>
                                            <option value="Laureles">Laureles</option>
                                            <option value="Estadio">Estadio</option>
                                            <option value="Aranjuez">Aranjuez</option>
                                            <option value="Castilla">Castilla</option>
                                            <option value="La Candelaria">La Candelaria</option>
                                            <option value="Guayabal">Guayabal</option>
                                            <option value="El Escobero">El Escobero</option>
                                            <option value="Las Palmas">Las Palmas</option>
                                        </select>
                                    </div>

                                    {/* 4. Rango de precio */}
                                    <div className="col flex-column d-flex gap-1">
                                        <span className="px-2 text-muted fw-bold" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            RANGO PRECIO
                                        </span>
                                        <select
                                            value={rangoPrecio}
                                            onChange={(e) => {
                                                setRangoPrecio(e.target.value);
                                                if (e.target.value) setCodigoInmueble('');
                                            }}
                                            className="form-select bg-white border-0 text-dark rounded-pill px-3 py-2 shadow-sm style-select-custom"
                                            style={{ fontSize: '13.5px', height: '44px' }}
                                        >
                                            <option value="">Todos</option>
                                            {tipoGestion === 'arriendo' ? (
                                                <>
                                                    <option value="1">&lt; $1.5M</option>
                                                    <option value="2">$1.5M – $3M</option>
                                                    <option value="3">&gt; $3M</option>
                                                </>
                                            ) : (
                                                <>
                                                    <option value="1">&lt; $300M</option>
                                                    <option value="2">$300M – $600M</option>
                                                    <option value="3">&gt; $600M</option>
                                                </>
                                            )}
                                        </select>
                                    </div>

                                    {/* 5. Código del inmueble */}
                                    <div className="col flex-column d-flex gap-1">
                                        <span className="px-2 text-muted fw-bold" style={{ fontSize: '11px', letterSpacing: '0.3px' }}>
                                            CÓDIGO
                                        </span>
                                        <input
                                            type="text"
                                            value={codigoInmueble}
                                            onChange={(e) => setCodigoInmueble(e.target.value)}
                                            placeholder="Escribe código"
                                            className="form-control bg-white border-0 text-dark rounded-pill px-3 py-2 shadow-sm"
                                            style={{ fontSize: '13.5px', height: '44px' }}
                                        />
                                    </div>

                                    {/* 6. Botón Buscar */}
                                    <div className="col d-grid">
                                        <button
                                            type="submit"
                                            className="btn btn-primary text-white rounded-pill d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm search-btn-hero"
                                            style={{ height: '44px' }}
                                        >
                                            <Search size={18} />
                                            <span>Buscar</span>
                                        </button>
                                    </div>

                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </section>

            <section id="aa-client-brand" className="my-5 py-4">
                {/* TÍTULO DE LA SECCIÓN */}
                <div className="text-center mb-5 max-w-2xl mx-auto">
                    <h2 className="fw-bold text-dark mb-2">Propiedades Agregadas Recientemente</h2>
                    <div className="bg-primary mx-auto mb-3 rounded-pill" style={{ width: '60px', height: '4px' }}></div>
                </div>

                {/* GRILLA DE PROPIEDADES */}
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
                    {listInmueble.map(Inmueble => (
                        <div key={Inmueble.CODIGO_INMUEBLE} className="col d-flex">

                            {/* TARJETA PRINCIPAL */}
                            <div
                                className="card w-100 border-0 shadow-sm p-3 bg-white d-flex flex-column text-center property-card"
                                style={{ borderRadius: '24px' }}
                            >

                                {/* 1. ENCABEZADO */}
                                <div className="pt-2 px-2 mb-2">
                                    <h2 style={{ color: "#b83535", margin: 0, fontWeight: "bold" }}>
                                        {Inmueble.TIPO_INMUEBLE}
                                    </h2>
                                    <h3 style={{ color: "#826c6c", margin: "2px 0" }}>
                                        {Inmueble.OPCION_INMUEBLE}
                                    </h3>
                                    <p className="text-muted" style={{ margin: 0 }}>
                                        {Inmueble.MUNICIPIO}, {Inmueble.BARRIO}
                                    </p>
                                </div>

                                {/* 2. CONTENEDOR DE LA IMAGEN */}
                                <div
                                    className="position-relative overflow-hidden property-img-container mb-3"
                                    style={{ height: "260px", borderRadius: "18px" }}
                                >
                                    <img
                                        src={`${BASE_URL}/dbimages/${Inmueble.IMAGEN_PRINCIPAL}`}
                                        alt={Inmueble.TIPO_INMUEBLE}
                                        onClick={() => clickToPropiedad(Inmueble.CODIGO_INMUEBLE)}
                                        className="w-100 h-100 property-img"
                                        style={{ objectFit: "cover", cursor: "pointer" }}
                                    />
                                    {/* Badge Cápsula con el código */}
                                    <span
                                        className="position-absolute top-0 start-0 m-3 bg-primary text-white fw-bold px-3 py-1 rounded-pill shadow-sm"
                                        style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                                    >
                                        CÓDIGO: {Inmueble.CODIGO_INMUEBLE}
                                    </span>
                                </div>

                                {/* 3. FRANJA ROJA CON ÍCONOS */}
                                <div
                                    className="py-2 px-3 text-white mb-3 shadow-sm"
                                    style={{ background: "#b83535", borderRadius: "16px" }}
                                >
                                    <div className="d-flex justify-content-around align-items-center">
                                        <span className="d-inline-flex align-items-center gap-1">
                                            <i className="fa fa-bed" style={{ fontSize: "18px" }}></i>
                                            <span className="fw-semibold" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "15px" }}>
                                                {Inmueble.HABITACIONES}
                                            </span>
                                        </span>

                                        <span className="d-inline-flex align-items-center gap-1">
                                            <i className="fa fa-car" style={{ fontSize: "18px" }}></i>
                                            <span className="fw-semibold" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "15px" }}>
                                                {Inmueble.PARQUEADERO}
                                            </span>
                                        </span>

                                        <span className="d-inline-flex align-items-center gap-1">
                                            <i className="fa fa-bath" style={{ fontSize: "18px" }}></i>
                                            <span className="fw-semibold" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "15px" }}>
                                                {Inmueble.BANOS}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                {/* 4. ÁREA Y PRECIO */}
                                <div className="mt-auto pt-2">
                                    <div className="row g-2 justify-content-center align-items-center">

                                        {/* ÁREA */}
                                        <div className="col-12 pb-2 border-bottom">
                                            <span className="fw-bold d-block text-secondary small">ÁREA / SUPERFICIE:</span>
                                            <span className="fw-semibold text-dark">{Inmueble.AREA} m²</span>
                                        </div>

                                        {/* PRECIO (Recuadro adaptable que resalta como el ejemplo) */}
                                        <div className="col-12 mt-2 p-2 rounded" style={{ backgroundColor: "#fdf8f8", border: "1px solid #f2d6d6" }}>
                                            <span className="fw-bold d-block text-secondary" style={{ fontSize: "11px" }}>PRECIO:</span>
                                            <h2 className="m-0 text-truncate fw-bold" style={{ color: "#826c6c" }}>
                                                {Inmueble.PRECIO}
                                            </h2>
                                        </div>

                                    </div>

                                    {/* BOTÓN / ENLACE DE INFORMACIÓN */}
                                    <div className="mt-3 pt-1 text-center">
                                        <Link
                                            to={`/propiedades/${Inmueble.CODIGO_INMUEBLE}`}
                                            className="text-decoration-none fw-semibold d-inline-flex align-items-center gap-1 info-link"
                                            style={{ color: "#b83535", fontSize: "14px" }}
                                        >
                                            <span>Ver más información</span>
                                            <i className="fa fa-arrow-right small"></i>
                                        </Link>
                                    </div>
                                </div>

                            </div>

                        </div>
                    ))}
                </div>
            </section>

            <section
                id="aa-promo-banner"
                className="bg-dark text-white py-5 rounded-5 shadow-lg overflow-hidden my-4"
            >
                <div className="container py-4">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="text-center mb-5 max-w-2xl mx-auto">
                                <h2 className="fw-bold mb-2 text-white">Nuestros Servicios</h2>
                                <div className="bg-primary mx-auto mb-3 rounded-pill" style={{ width: '60px', height: '4px' }}></div>
                                <p className="text-light opacity-75">
                                    En Arrendamientos Superbienes te brindamos soluciones inmobiliarias integrales con el respaldo, agilidad y transparencia que te mereces.
                                </p>
                            </div>

                            <div className="row g-4">
                                {SERVICIOS.map((servicio) => (
                                    <div className="col-12 col-md-6 col-lg-4" key={servicio.id}>
                                        <div className="card h-100 border-0 shadow-sm p-4 rounded-4 text-center bg-white service-card">
                                            <div
                                                className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle p-3 mb-3 mx-auto icon-wrapper"
                                                style={{ width: '64px', height: '64px' }}
                                            >
                                                {servicio.icon}
                                            </div>
                                            <h4 className="fw-bold text-dark h5 mb-3">{servicio.title}</h4>
                                            <p className="text-muted small mb-0 text-start leading-relaxed">{servicio.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="aa-agents" className="bg-light py-5">
                <div className="container py-4">
                    <div className="row">
                        <div className="col-md-12">
                            {/* Cabecera de la Sección */}
                            <div className="text-center mb-5 max-w-2xl mx-auto">
                                <h2 className="fw-bold text-dark mb-2">Solicitudes de Arrendamiento</h2>
                                <div className="bg-primary mx-auto mb-3 rounded-pill" style={{ width: '60px', height: '4px' }}></div>
                                <p className="text-muted fs-5">
                                    Descargue los formatos necesarios para iniciar su trámite con nosotros.
                                </p>
                            </div>

                            {/* Grid de Formatos/Documentos */}
                            <div className="row g-4 justify-content-center">
                                {FORMATOS_ARRENDAMIENTO.map((formato) => (
                                    <div className="col-12 col-sm-6 col-md-4" key={formato.id}>
                                        <div
                                            className="card border-0 shadow-sm overflow-hidden bg-white h-100 d-flex flex-column justify-content-between document-card"
                                            style={{ borderRadius: '24px' }}
                                        >
                                            <div className="p-4 text-center">
                                                {/* Contenedor del Icono de Documento */}
                                                <div
                                                    className="bg-light rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4 icon-wrapper"
                                                    style={{ width: '90px', height: '90px' }}
                                                >
                                                    <FileText size={42} className="text-primary" />
                                                </div>

                                                {/* Detalles del Formato */}
                                                <span className="badge bg-danger-subtle text-danger mb-2 px-3 py-2 rounded-pill fw-semibold">
                                                    {formato.type}
                                                </span>
                                                <h4 className="fw-bold h5 mb-2 text-dark">{formato.title}</h4>
                                                <p className="text-muted small mb-0">{formato.description}</p>
                                            </div>

                                            {/* Footer de la tarjeta con botón de descarga */}
                                            <div className="card-footer bg-white border-0 p-4 pt-0 text-center">
                                                <div className="text-muted small mb-3">
                                                    Tamaño: <strong>{formato.fileSize}</strong>
                                                </div>

                                                <a
                                                    href={formato.fileUrl}
                                                    onClick={(e) => handleDownload(e, formato.fileUrl, `${formato.nombre || formato.title}.pdf`)}
                                                    className="btn btn-primary text-white w-100 py-2 d-flex align-items-center justify-content-center gap-2 fw-medium text-decoration-none custom-btn"
                                                    style={{ borderRadius: '14px' }}
                                                >
                                                    <Download size={18} />
                                                    Descargar Formato
                                                </a>
                                            </div>

                                        </div>
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            <section id="aa-client-testimonial" className="py-5 bg-white">
                <div className="container py-4">
                    <div className="row">
                        <div className="col-md-12">
                            {/* Cabecera de la Sección */}
                            <div className="text-center mb-5 max-w-2xl mx-auto">
                                <h2 className="fw-bold mb-2 text-white">Actualidad y Consejos</h2>
                                <div className="bg-primary mx-auto mb-3 rounded-pill" style={{ width: '60px', height: '4px' }}></div>
                                <p className="text-light opacity-75">
                                    Mantente informado con las últimas novedades del sector inmobiliario, tips legales y guías de vivienda.
                                </p>
                            </div>

                            {/* Grid de Noticias */}
                            <div className="row g-4">
                                {NOTICIAS.map((noticia) => (
                                    <div className="col-12 col-md-4" key={noticia.id}>
                                        <article
                                            className="card h-100 border-0 shadow-sm overflow-hidden bg-white d-flex flex-column news-card"
                                            style={{ borderRadius: '24px' }}
                                        >
                                            {/* Imagen con contenedor redondeado superior */}
                                            <div className="position-relative overflow-hidden img-wrapper" style={{ height: '220px' }}>
                                                <img
                                                    src={noticia.img}
                                                    alt={noticia.title}
                                                    className="w-100 h-100 object-fit-cover news-img"
                                                />
                                                <span className="position-absolute bottom-0 start-0 bg-primary text-white px-3 py-1 small m-3 rounded-pill shadow-sm d-flex align-items-center gap-1">
                                                    <Calendar size={14} /> {noticia.date}
                                                </span>
                                            </div>

                                            {/* Cuerpo de la tarjeta */}
                                            <div className="card-body p-4 d-flex flex-column justify-content-between flex-grow-1">
                                                <div>
                                                    <h3 className="h5 fw-bold mb-3 text-dark line-clamp-2">
                                                        <a href="#" className="text-decoration-none text-dark hover-primary">{noticia.title}</a>
                                                    </h3>
                                                    <p className="text-muted small card-text-justify mb-4 line-clamp-3">{noticia.desc}</p>
                                                </div>
                                                <div className="d-flex align-items-center justify-content-between pt-3 border-top border-light mt-auto">
                                                    <span className="text-muted small d-flex align-items-center gap-1">
                                                        <User size={14} /> Por {noticia.author}
                                                    </span>
                                                    <a href={noticia.url} target="_blank" rel="noopener noreferrer" className="btn btn-link text-primary p-0 fw-semibold d-flex align-items-center gap-1 text-decoration-none mt-2 read-more-btn">
                                                        Leer más <ArrowRight size={16} className="arrow-icon" />
                                                    </a>
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </Fragment>
    );
};

export default Index;